/**
 * Replay Jobs tab — list of historical replay jobs with live polling and
 * a drawer showing the job's events.
 */
import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, X } from "lucide-react";

import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import {
  useCancelReplayJob,
  useDeleteReplayJob,
  useReplayJob,
  useReplayJobs,
  useSubmitAdhocReplay,
} from "@/hooks/security/useHunts";
import type { ReplayJob, ReplayJobStatus, HuntEvent } from "@/types/edrHunts";

interface ReplayJobsTabProps {
  selectedJobId?: string;
  onSelectJob: (id: string | null) => void;
}

const STATUS_COLOR: Record<ReplayJobStatus, string> = {
  queued: "bg-muted text-muted-foreground border-border",
  running: "bg-info/10 text-info border-info/30 animate-pulse",
  complete: "bg-success/10 text-success border-success/30",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

export function ReplayJobsTab({ selectedJobId, onSelectJob }: ReplayJobsTabProps) {
  // Poll the list every 5s so progress bars on running jobs stay fresh.
  const jobsQ = useReplayJobs(5_000);
  const jobs = jobsQ.data ?? [];

  return (
    <>
      <Panel
        title={`Replay jobs · ${jobs.length}`}
        loading={jobsQ.isLoading}
        isError={jobsQ.isError}
        error={jobsQ.error}
        onRetry={() => jobsQ.refetch()}
        empty={!jobsQ.isLoading && !jobsQ.isError && jobs.length === 0}
        emptyTitle="No replay jobs"
        emptyDescription="Run a saved hunt against a 7d / 30d window and the job will appear here."
        skeleton="table"
      >
        <ReplayJobsTable
          jobs={jobs}
          onOpen={(j) => onSelectJob(j.id)}
        />
      </Panel>

      <Sheet open={!!selectedJobId} onOpenChange={(o) => !o && onSelectJob(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          {selectedJobId && <ReplayJobDrawer id={selectedJobId} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

function ReplayJobsTable({
  jobs,
  onOpen,
}: {
  jobs: ReplayJob[];
  onOpen: (j: ReplayJob) => void;
}) {
  const cancelM = useCancelReplayJob();
  const deleteM = useDeleteReplayJob();
  const reRunM = useSubmitAdhocReplay();

  return (
    <div className="overflow-x-auto -mx-4 -my-4">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 text-left">Hunt / Query</th>
            <th className="px-3 py-2 text-left w-[70px]">Window</th>
            <th className="px-3 py-2 text-left w-[100px]">Status</th>
            <th className="px-3 py-2 text-left w-[180px]">Progress</th>
            <th className="px-3 py-2 text-left w-[80px]">Matches</th>
            <th className="px-3 py-2 text-left w-[180px]">Submitted</th>
            <th className="px-3 py-2 text-left w-[90px]">Elapsed</th>
            <th className="px-3 py-2 text-right w-[60px]" />
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const elapsed = elapsedLabel(j);
            const window = j.windowHours ? (j.windowHours >= 24 ? `${j.windowHours / 24}d` : `${j.windowHours}h`) : "—";
            return (
              <tr
                key={j.id}
                className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
                onClick={() => onOpen(j)}
              >
                <td className="px-3 py-2 min-w-0 max-w-[260px]">
                  <div className="font-medium text-foreground truncate">{j.huntName ?? "Ad-hoc query"}</div>
                  <code className="block text-[10px] font-mono text-muted-foreground truncate">
                    {j.queryDsl ?? "—"}
                  </code>
                </td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">{window}</td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                      STATUS_COLOR[j.status] ?? STATUS_COLOR.queued,
                    )}
                  >
                    {j.status}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {j.status === "running" || j.status === "queued" ? (
                    <div className="space-y-1">
                      <Progress value={Math.round((j.progress ?? 0) * 100)} className="h-1" />
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {Math.round((j.progress ?? 0) * 100)}%
                        {j.scannedCount != null && ` · ${formatNum(j.scannedCount)} events`}
                      </div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground">—</div>
                  )}
                </td>
                <td className="px-3 py-2 font-semibold tabular-nums text-foreground">
                  {j.matchCount ?? 0}
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate">
                  <div className="truncate">{j.submittedBy ?? "—"}</div>
                  <div className="text-[10px]">
                    {j.startedAt
                      ? formatDistanceToNow(new Date(j.startedAt), { addSuffix: true })
                      : j.queuedAt
                      ? formatDistanceToNow(new Date(j.queuedAt), { addSuffix: true })
                      : "—"}
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">{elapsed}</td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onOpen(j)}>View results</DropdownMenuItem>
                      {j.status === "running" && (
                        <DropdownMenuItem
                          onClick={() =>
                            cancelM.mutate(j.id, {
                              onSuccess: () => toast.success("Job cancelled"),
                              onError: (e: any) => toast.error(e?.message ?? "Failed to cancel"),
                            })
                          }
                        >
                          Cancel job
                        </DropdownMenuItem>
                      )}
                      {j.status === "failed" && j.queryDsl && j.windowHours && (
                        <DropdownMenuItem
                          onClick={() =>
                            reRunM.mutate(
                              { queryDsl: j.queryDsl!, windowHours: j.windowHours! },
                              {
                                onSuccess: () => toast.success("Job re-queued"),
                                onError: (e: any) => toast.error(e?.message ?? "Failed"),
                              },
                            )
                          }
                        >
                          Re-run
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() =>
                          deleteM.mutate(j.id, {
                            onSuccess: () => toast.success("Job deleted"),
                            onError: (e: any) => toast.error(e?.message ?? "Failed"),
                          })
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReplayJobDrawer({ id }: { id: string }) {
  const jobQ = useReplayJob(id, true);
  const j = jobQ.data;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {j?.huntName ?? "Replay job"}
          {j?.status && (
            <span
              className={cn(
                "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                STATUS_COLOR[j.status],
              )}
            >
              {j.status}
            </span>
          )}
        </SheetTitle>
        <SheetDescription>
          {j?.queryDsl ? (
            <code className="block mt-1 font-mono text-[11px] break-all">{j.queryDsl}</code>
          ) : (
            "Loading job details…"
          )}
        </SheetDescription>
      </SheetHeader>

      {jobQ.isLoading && (
        <div className="py-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      )}

      {j && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Metric label="Matches" value={String(j.matchCount ?? 0)} />
            <Metric label="Scanned" value={formatNum(j.scannedCount ?? 0)} />
            <Metric
              label="Window"
              value={j.windowHours ? (j.windowHours >= 24 ? `${j.windowHours / 24}d` : `${j.windowHours}h`) : "—"}
            />
          </div>

          {(j.status === "running" || j.status === "queued") && (
            <div>
              <Progress value={Math.round((j.progress ?? 0) * 100)} className="h-1.5" />
              <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                {Math.round((j.progress ?? 0) * 100)}%
              </div>
            </div>
          )}

          {j.status === "failed" && j.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {j.error}
            </div>
          )}

          {j.events && j.events.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                Events ({j.events.length})
              </div>
              <JobEventsTable events={j.events} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

function JobEventsTable({ events }: { events: HuntEvent[] }) {
  return (
    <div className="border border-border rounded-md overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-1.5 text-left">Time</th>
            <th className="px-2 py-1.5 text-left">Device</th>
            <th className="px-2 py-1.5 text-left">Process</th>
          </tr>
        </thead>
        <tbody>
          {events.slice(0, 200).map((e, i) => (
            <tr key={e.id ?? i} className="border-t border-border/60">
              <td className="px-2 py-1.5 text-muted-foreground tabular-nums">
                {e.receivedAt ? formatDistanceToNow(new Date(e.receivedAt), { addSuffix: true }) : "—"}
              </td>
              <td className="px-2 py-1.5 font-mono truncate max-w-[140px]">
                {e.hostname ?? e.deviceId ?? "—"}
              </td>
              <td className="px-2 py-1.5 font-mono truncate max-w-[240px]" title={e.process?.path}>
                {e.process?.path ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function elapsedLabel(j: ReplayJob): string {
  if (!j.startedAt) return "—";
  const end = j.completedAt ? new Date(j.completedAt).getTime() : Date.now();
  const start = new Date(j.startedAt).getTime();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
